import Konva from 'konva';
import { memo, useEffect, useState } from 'react';
import { Image } from 'react-konva';
import useImage from 'use-image';

import { INIT_SCALE, KonvaImage, MAX_SCALE, MIN_SCALE } from '.';
import { getAbsolutePosition } from './util';

interface IProps {
  item: KonvaImage;
  disabled: boolean;
  bgSize: { width: number; height: number };
  imageRefs: React.MutableRefObject<Map<string, Konva.Image>>;
  transformerRef: React.MutableRefObject<Konva.Transformer | null>;
  onSelect?: (id: string) => void;
  onTransform?: (id: string) => void;
}

// konva图片组件
const CustomImage: React.FC<IProps> = memo((props) => {
  const {
    item,
    disabled,
    bgSize,
    imageRefs,
    transformerRef,
    onSelect,
    onTransform,
  } = props;
  const { id, url } = item;
  const [image] = useImage(url, 'anonymous');
  const [initSize, setInitSize] = useState({ width: 0, height: 0 });

  // 尺寸和坐标计算
  useEffect(() => {
    const img = new window.Image();
    img.src = url;
    img.onload = () => {
      const scale = Math.min(
        bgSize.width / img.naturalWidth,
        bgSize.height / img.naturalHeight,
      );
      setInitSize({
        width: img.naturalWidth * scale,
        height: img.naturalHeight * scale,
      });

      const node = imageRefs.current.get(id);
      if (node) {
        const { x, y } = getAbsolutePosition(
          bgSize,
          item.x || 0,
          item.y || 0,
          img.naturalWidth * scale,
          img.naturalHeight * scale,
          node.scaleX(),
        );
        node.x(x);
        node.y(y);
        node.getLayer()?.batchDraw();
      }
    };
  }, [bgSize, url, id, imageRefs, item]);

  // 组件卸载时移除引用
  useEffect(() => () => {
    imageRefs.current?.delete(id);

    if (transformerRef.current) {
      const nodes = transformerRef.current.nodes();
      if (nodes.some(n => n.id() === id)) {
        const newNodes = nodes.filter(n => n.id() !== id);
        transformerRef.current.nodes(newNodes);
        transformerRef.current.getLayer()?.batchDraw();
      }
    }
  }, [id]);

  const handleImageClick = (id: string, e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;
    // 更新transformer绑定
    const node = imageRefs.current.get(id);
    if (node && transformerRef.current) {
      transformerRef.current.nodes([node]);
      transformerRef.current.getLayer()?.batchDraw();
    }
    onSelect?.(id);
  };

  const handleTransformEnd = (id: string) => {
    const node = imageRefs.current.get(id);
    if (!node) return;

    const newScale = Math.min(Math.max(node.scaleX(), MIN_SCALE), MAX_SCALE);
    node.scaleX(newScale);
    node.scaleY(newScale);

    onTransform?.(id);
  };

  const handleDragEnd = (id: string) => {
    onTransform?.(id);
  };

  return (
      <Image
        id={id}
        image={image}
        {...initSize}
        draggable={!disabled}
        ref={(node) => {
          if (node) {
            if (!initSize.width || !initSize.height) return;

            const existingNode = imageRefs.current?.get(id);
            if (existingNode) {
              node.x(existingNode.x());
              node.y(existingNode.y());
              node.scaleX(existingNode.scaleX());
              node.scaleY(existingNode.scaleY());
            } else {
              const scale = item.scale || INIT_SCALE;
              const { x, y } = getAbsolutePosition(
                bgSize,
                item.x || 0,
                item.y || 0,
                initSize.width,
                initSize.height,
                scale,
              );
              node.x(x);
              node.y(y);
              node.scaleX(scale);
              node.scaleY(scale);
            }
            imageRefs.current?.set(id, node);
          }
        }}
        onTransformEnd={() => handleTransformEnd(id)}
      onDragEnd={() => handleDragEnd(id)}
        onClick={(e: Konva.KonvaEventObject<MouseEvent>) => handleImageClick(id, e)}
      />
  );
});

export default CustomImage;
